/**
 *
    Below functions borrowed from https://github.com/chriso/validator.js/tree/master/src/lib
    https://github.com/chriso/validator.js
    PW modifications:
    - Cherry picked only the validator functions we need, plus supporting utils.
    - Preserve src ES6 import/export syntax
 */

/**
 *
Copyright (c) 2016 Chris O'Hara <cohara87@gmail.com>

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND
NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE
LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION
OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION
WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
 */

// Utils
function merge (obj = { }, defaults) {
  for (const key in defaults) {
    if (typeof obj[key] === 'undefined') {
      obj[key] = defaults[key]
    }
  }
  return obj
}

function assertString (input) {
  const isString = (typeof input === 'string' || input instanceof String)

  if (!isString) {
    throw new TypeError('This library (validator.js) validates strings only')
  }
}

const defaultFqdnOptions = {
  require_tld: true,
  allow_underscores: false,
  allow_trailing_dot: false
}

export const isFQDN = (str, options) => {
  assertString(str)
  options = merge(options, defaultFqdnOptions)

  /* Remove the optional trailing dot before checking validity */
  if (options.allow_trailing_dot && str[str.length - 1] === '.') {
    str = str.substring(0, str.length - 1)
  }
  const parts = str.split('.')
  if (options.require_tld) {
    const tld = parts.pop()
    if (!parts.length || !/^([a-z\u00a1-\uffff]{2,}|xn[a-z0-9-]{2,})$/i.test(tld)) {
      return false
    }
    // disallow spaces
    if (/[\s\u2002-\u200B\u202F\u205F\u3000\uFEFF\uDB40\uDC20]/.test(tld)) { /* eslint-disable-line */

      return false
    }
  }
  for (let part, i = 0; i < parts.length; i++) {
    part = parts[i]
    if (options.allow_underscores) {
      part = part.replace(/_/g, '')
    }
    if (!/^[a-z\u00a1-\uffff0-9-]+$/i.test(part)) {
      return false
    }
    // disallow full-width chars
    if (/[\uff01-\uff5e]/.test(part)) {
      return false
    }
    if (part[0] === '-' || part[part.length - 1] === '-') {
      return false
    }
  }
  return true
}

const defaultUrlOptions = {
  protocols: ['http', 'https', 'ftp'],
  require_tld: true,
  require_protocol: false,
  require_host: true,
  require_valid_protocol: true,
  allow_underscores: false,
  allow_trailing_dot: false,
  allow_protocol_relative_urls: false
}

const wrappedIpv6 = /^\[([^\]]+)\](?::([0-9]+))?$/

function isRegExp (obj) {
  return Object.prototype.toString.call(obj) === '[object RegExp]'
}

function checkHost (host, matches) {
  for (let i = 0; i < matches.length; i++) {
    const match = matches[i]
    if (host === match || (isRegExp(match) && match.test(host))) {
      return true
    }
  }
  return false
}

export const isURL = (url, options) => {
  assertString(url)
  if (!url || url.length >= 2083 || /[\s<>]/.test(url)) {
    return false
  }
  if (url.indexOf('mailto:') === 0) {
    return false
  }
  options = merge(options, defaultUrlOptions)

  let protocol, auth, host, port, portStr, split, ipv6

  split = url.split('#')
  url = split.shift()

  split = url.split('?')
  url = split.shift()

  split = url.split('://')
  if (split.length > 1) {
    protocol = split.shift()
    if (options.require_valid_protocol && options.protocols.indexOf(protocol) === -1) {
      return false
    }
  } else if (options.require_protocol) {
    return false
  } else if (options.allow_protocol_relative_urls && url.substr(0, 2) === '//') {
    split[0] = url.substr(2)
  }
  url = split.join('://')

  if (url === '') {
    return false
  }

  split = url.split('/')
  url = split.shift()

  if (url === '' && !options.require_host) {
    return true
  }

  split = url.split('@')
  if (split.length > 1) {
    auth = split.shift()
    if (auth.indexOf(':') >= 0 && auth.split(':').length > 2) {
      return false
    }
  }
  const hostname = split.join('@')

  portStr = null
  ipv6 = null
  const ipv6Match = hostname.match(wrappedIpv6)
  if (ipv6Match) {
    host = ''
    ipv6 = ipv6Match[1]
    portStr = ipv6Match[2] || null
  } else {
    split = hostname.split(':')
    host = split.shift()
    if (split.length) {
      portStr = split.join(':')
    }
  }

  if (portStr !== null) {
    port = parseInt(portStr, 10)
    if (!/^[0-9]+$/.test(portStr) || port <= 0 || port > 65535) {
      return false
    }
  }

  if (!isIP(host) && !isFQDN(host, options) && (!ipv6 || !isIP(ipv6, 6))) {
    return false
  }

  host = host || ipv6

  if (options.host_whitelist && !checkHost(host, options.host_whitelist)) {
    return false
  }
  if (options.host_blacklist && checkHost(host, options.host_blacklist)) {
    return false
  }

  return true
}

const ipv4Maybe = /^(\d{1,3})\.(\d{1,3})\.(\d{1,3})\.(\d{1,3})$/
const ipv6Block = /^[0-9A-F]{1,4}$/i

export const isIP = (str, version = '') => {
  assertString(str)
  version = String(version)
  if (!version) {
    return isIP(str, 4) || isIP(str, 6)
  } else if (version === '4') {
    if (!ipv4Maybe.test(str)) {
      return false
    }
    const parts = str.split('.').sort((a, b) => a - b)
    return parts[3] <= 255
  } else if (version === '6') {
    const blocks = str.split(':')
    let foundOmissionBlock = false // marker to indicate ::

    // At least some OS accept the last 32 bits of an IPv6 address
    // (i.e. 2 of the blocks) in IPv4 notation, and RFC 3493 says
    // that '::ffff:a.b.c.d' is valid for IPv4-mapped IPv6 addresses,
    // and '::a.b.c.d' is deprecated, but also valid.
    const foundIPv4TransitionBlock = isIP(blocks[blocks.length - 1], 4)
    const expectedNumberOfBlocks = foundIPv4TransitionBlock ? 7 : 8

    if (blocks.length > expectedNumberOfBlocks) {
      return false
    }
    // initial or final ::
    if (str === '::') {
      return true
    } else if (str.substr(0, 2) === '::') {
      blocks.shift()
      blocks.shift()
      foundOmissionBlock = true
    } else if (str.substr(str.length - 2) === '::') {
      blocks.pop()
      blocks.pop()
      foundOmissionBlock = true
    }

    for (let i = 0; i < blocks.length; ++i) {
      // test for a :: which can not be at the string start/end
      // since those cases have been handled above
      if (blocks[i] === '' && i > 0 && i < blocks.length - 1) {
        if (foundOmissionBlock) {
          return false // multiple :: in address
        }
        foundOmissionBlock = true
      } else if (foundIPv4TransitionBlock && i === blocks.length - 1) {
        // it has been checked before that the last
        // block is a valid IPv4 address
      } else if (!ipv6Block.test(blocks[i])) {
        return false
      }
    }
    if (foundOmissionBlock) {
      return blocks.length >= 1
    }
    return blocks.length === expectedNumberOfBlocks
  }
  return false
}

const macAddress = /^([0-9a-fA-F][0-9a-fA-F]:){5}([0-9a-fA-F][0-9a-fA-F])$/

export const isMACAddress = (str) => {
  assertString(str)
  return macAddress.test(str)
}
